import { forwardRef, useState } from 'react';
import { AlertTriangle, CalendarClock, CalendarDays, CheckCircle2, Download, FileText, Hourglass } from 'lucide-react';

import { EmptyState } from '@/components/ui/EmptyState';
import { toast } from '@/hooks/useToast';
import { downloadPrivateFile } from '@/lib/download';
import { cn } from '@/lib/utils';
import { parentApi } from '../../api/parent.api';
import type { ParentChild, ParentWorkItem } from '../../types/parent';
import {
  WORK_STATE_LABEL,
  formatDueDay,
  formatFileSize,
  formatSubmittedAt,
  workState,
} from '../../utils';

export interface WorkDetailsProps {
  /** Null before anything is picked, and when the filters leave nothing to pick. */
  item: ParentWorkItem | null;
  child: ParentChild;
  /** Today as `YYYY-MM-DD`, passed in so every panel on the screen agrees. */
  today: string;
  /** What the list calls one of these — "assignment" or "task". */
  noun: string;
  /**
   * Drops the panel and the heading, for a caller that already draws both.
   *
   * The Tasks tab opens this inside a `Modal`, which supplies the surface and
   * the title; without this the reader gets a card inside a dialog and the
   * title twice. The status badge moves into the body, because that is the one
   * thing the dialog's own header cannot say.
   */
  bare?: boolean;
}

/**
 * Everything about the one piece of work the parent has opened.
 *
 * Beside the list rather than behind a dialog: a parent reads down the list and
 * opens three or four in a row, and a modal makes that four opens and four
 * closes. On a phone the two stack and this panel sits under the list, which is
 * why the page scrolls it into view when a row is tapped.
 *
 * The ref is that scroll target and is the only reason this is a `forwardRef`.
 */
export const WorkDetails = forwardRef<HTMLElement, WorkDetailsProps>(function WorkDetails(
  { item, child, today, noun, bare = false },
  ref,
) {
  /** Which of the two files is on its way down, so its button can say so. */
  const [downloading, setDownloading] = useState<'sheet' | 'submission' | null>(null);

  if (!item) {
    return (
      <section
        ref={ref}
        className={cn(
          !bare &&
            'rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 shadow-xs',
        )}
        aria-label={`${noun} details`}
      >
        <EmptyState
          icon={<FileText className="h-5 w-5" />}
          title="Nothing open"
          description={`Pick a row from the list to read the whole ${noun} — what was set, when it is due, and whether it has been handed in.`}
        />
      </section>
    );
  }

  const state = workState(item, today);
  /** The child's own name, not "you" — the reader is the parent, not the pupil. */
  const [firstName] = child.fullName.split(' ');

  /**
   * Homework and activities keep their files under different routes, and the
   * type is what says which table a row came from.
   */
  const isHomework = item.type === 'HOMEWORK';

  /**
   * Fetches one of the two private files with the access token and saves it.
   * The server checks the child is this parent's and the work is that child's
   * section's before a byte is read (CLAUDE.md §10, §11).
   */
  const download = async (which: 'sheet' | 'submission', fileName: string) => {
    const path =
      which === 'sheet'
        ? isHomework
          ? parentApi.worksheetPath(child.uuid, item.uuid)
          : parentApi.taskGuidelinesPath(child.uuid, item.uuid)
        : isHomework
          ? parentApi.homeworkSubmissionPath(child.uuid, item.uuid)
          : parentApi.taskSubmissionPath(child.uuid, item.uuid);

    setDownloading(which);
    try {
      await downloadPrivateFile(path, fileName);
    } catch {
      toast.error('Could not download that file', 'Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <section
      ref={ref}
      className={cn(
        !bare &&
          'rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 sm:p-6 shadow-xs',
        'flex flex-col gap-5',
      )}
      aria-label={`${noun} details`}
    >
      {/* --- Header / Title & Status ------------------------------------ */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3.5 min-w-0">
          <span
            aria-hidden
            className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400"
          >
            <FileText className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              <span className="text-blue-600 dark:text-blue-400 font-bold">{item.subject}</span> · {item.teacher}
            </p>
            <h3 className="mt-0.5 text-lg sm:text-xl font-bold tracking-tight text-slate-900 dark:text-white">
              {item.title}
            </h3>
          </div>
        </div>

        <span
          className={cn(
            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold shrink-0',
            state === 'COMPLETED' &&
              'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20',
            state === 'OVERDUE' &&
              'bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-500/20',
            (state === 'PENDING' || state === 'UPCOMING') &&
              'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20',
          )}
        >
          {WORK_STATE_LABEL[state]}
        </span>
      </div>

      {/* --- Due & Assigned Date Row ------------------------------------ */}
      <div className="rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 p-3 flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm font-semibold">
        <span className="inline-flex items-center gap-1.5 text-indigo-600 dark:text-indigo-400">
          <CalendarClock className="h-4 w-4" aria-hidden />
          <span>Due: {formatDueDay(item.dueDate)}</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
          <CalendarDays className="h-4 w-4" aria-hidden />
          <span>Assigned: {formatDueDay(item.assignedDate)}</span>
        </span>
      </div>

      {/* --- Description ----------------------------------------------- */}
      {/* Left out when the teacher wrote none, rather than a heading over nothing. */}
      {item.description && (
        <div>
          <h4 className="text-sm font-bold text-slate-900 dark:text-white">Description</h4>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
            {item.description}
          </p>
        </div>
      )}

      {/* --- Instructions ---------------------------------------------- */}
      {item.instructions.length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-slate-900 dark:text-white">Instructions</h4>
          <ul className="mt-1.5 flex list-disc flex-col gap-1 pl-5 text-sm leading-relaxed text-slate-600 dark:text-slate-300 marker:text-indigo-600 dark:marker:text-indigo-400">
            {item.instructions.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      {/* --- Attachment ------------------------------------------------ */}
      {item.attachment && (
        <div>
          <h4 className="text-sm font-bold text-slate-900 dark:text-white">Attachments</h4>
          <div className="mt-1.5 flex items-center justify-between gap-3 rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 p-3.5">
            <div className="flex items-center gap-3 min-w-0">
              <span
                aria-hidden
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20"
              >
                <FileText className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                  {item.attachment.fileName}
                </p>
                {/* Only when the size was recorded — "0 B" beside a real file
                    reads as a broken upload. */}
                {item.attachment.sizeBytes !== null && (
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                    {formatFileSize(item.attachment.sizeBytes)}
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                if (item.attachment) void download('sheet', item.attachment.fileName);
              }}
              disabled={downloading === 'sheet'}
              aria-busy={downloading === 'sheet'}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3.5 py-1.5 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              <Download className="h-3.5 w-3.5" aria-hidden />
              <span>{downloading === 'sheet' ? 'Downloading…' : 'Download'}</span>
            </button>
          </div>
        </div>
      )}

      {/* --- Submission ------------------------------------------------ */}
      <div>
        <h4 className="text-sm font-bold text-slate-900 dark:text-white">Submission</h4>
        <div
          className={cn(
            'mt-1.5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-3.5',
            item.submittedAt
              ? 'border-emerald-200/90 dark:border-emerald-900/40 bg-emerald-50/80 dark:bg-emerald-950/30'
              : state === 'OVERDUE'
                ? 'border-rose-200/90 dark:border-rose-900/40 bg-rose-50/80 dark:bg-rose-950/30'
                : 'border-amber-200/90 dark:border-amber-900/40 bg-amber-50/80 dark:bg-amber-950/30',
          )}
        >
          {item.submittedAt ? (
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
              <span>
                Submitted by {firstName} on {formatSubmittedAt(item.submittedAt)}
              </span>
            </p>
          ) : (
            // Not handed in: said as plainly as the submitted case, in the
            // colour of its badge, so a parent sees at a glance whether it is
            // late or still has time.
            <div className="flex items-start gap-3">
              <span
                className={cn(
                  'grid h-10 w-10 shrink-0 place-items-center rounded-xl border',
                  state === 'OVERDUE'
                    ? 'border-rose-200 dark:border-rose-800/60 bg-white dark:bg-rose-950/40 text-rose-600 dark:text-rose-400'
                    : 'border-amber-200 dark:border-amber-800/60 bg-white dark:bg-amber-950/40 text-amber-600 dark:text-amber-400',
                )}
                aria-hidden
              >
                {state === 'OVERDUE' ? <AlertTriangle className="h-5 w-5" /> : <Hourglass className="h-5 w-5" />}
              </span>
              <div className="min-w-0">
                <p
                  className={cn(
                    'text-sm font-bold',
                    state === 'OVERDUE' ? 'text-rose-700 dark:text-rose-300' : 'text-amber-700 dark:text-amber-300',
                  )}
                >
                  Not submitted yet
                </p>
                <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
                  {state === 'OVERDUE'
                    ? `${firstName} has not handed this in. It was due ${formatDueDay(item.dueDate)}.`
                    : `${firstName} has not handed this in yet. It is due ${formatDueDay(item.dueDate)}.`}
                </p>
              </div>
            </div>
          )}
          {/* Only a handed-in file can be opened — a repository link or a
              model on the teacher's desk has nothing to download. */}
          {item.submittedAt && item.submissionFileName && (
            <button
              type="button"
              onClick={() => {
                if (item.submissionFileName) void download('submission', item.submissionFileName);
              }}
              disabled={downloading === 'submission'}
              aria-busy={downloading === 'submission'}
              className="rounded-xl bg-emerald-800 tracking-wide hover:bg-emerald-700 text-white px-3.5 py-1.5 text-sm font-bold shadow-xs transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
            >
              {downloading === 'submission' ? 'Downloading…' : 'Download submission'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
});
